import { ResponsiveBar } from '@nivo/bar'
import { useMemo, useEffect, useState } from 'react'
import styles from './DailyTimeChart.module.css'
import { moment } from '../utils/dateUtils'

export default function DailyTimeChart({ dateRange, filters, chartData }) {
    const [currentTheme, setCurrentTheme] = useState('light')

    // Detect current theme
    useEffect(() => {
        const detectTheme = () => {
            const root = document.documentElement
            const themeAttr = root.getAttribute('data-theme')
            setCurrentTheme(themeAttr || 'light')
        }
        
        detectTheme()
        
        // Watch for theme changes
        const observer = new MutationObserver(detectTheme)
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        })
        
        return () => observer.disconnect()
    }, [])

    // Generate dynamic color palette based on theme
    const getThemeColorPalette = () => {
        switch (currentTheme) {
            case 'dark':
                return [
                    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#F9CA24', 
                    '#F0932B', '#EB4D4B', '#6C5CE7', '#A29BFE', '#FD79A8',
                    '#FDCB6E', '#E17055', '#81ECEC', '#74B9FF', '#55A3FF'
                ]
            case 'nightowl':
                return [
                    '#FF5370', '#82AAFF', '#C792EA', '#ADDB67', '#F78C6C',
                    '#FFCB8B', '#C3E88D', '#89DDFF', '#FF9CAC', '#B2CCD6',
                    '#EEFFFF', '#F07178', '#DECB6B', '#DD6B68', '#7E57C2'
                ]
            case 'pastel':
                return [
                    '#FFB3BA', '#BFEFFF', '#FFFFBA', '#BAE1FF', '#FFDFBA',
                    '#BAFFC9', '#FFB3E6', '#E6BAFF', '#BAFFFF', '#C9BAFF',
                    '#FFBABA', '#BAD7FF', '#D1BAFF', '#BAFFBA', '#FFBAF0'
                ]
            case 'gamer':
                return [
                    '#00FF88', '#FF0080', '#00D4FF', '#FFEB3B', '#FF4081',
                    '#00BCD4', '#4CAF50', '#E91E63', '#2196F3', '#9C27B0',
                    '#FF5722', '#795548', '#607D8B', '#FFC107', '#8BC34A'
                ]
            default: // light theme
                return [
                    '#E74C3C', '#3498DB', '#9B59B6', '#27AE60', '#F39C12',
                    '#E67E22', '#1ABC9C', '#34495E', '#2C3E50', '#8E44AD',
                    '#16A085', '#D35400', '#C0392B', '#7F8C8D', '#2980B9'
                ]
        }
    }

    // Generate color for a specific item (user, project, etc.)
    const generateColor = (item, index, type = 'user') => {
        const palette = getThemeColorPalette()
        
        // Create a hash from the item name/id for consistent colors
        let hash = 0
        const str = item.name || item.key || item.id || `${type}_${index}`
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash // Convert to 32bit integer
        }
        
        // Use hash to select color consistently
        const colorIndex = Math.abs(hash) % palette.length
        return palette[colorIndex]
    }

    // Check if a date is a holiday (US holidays)
    const isHoliday = (date) => {
        const dateMoment = moment(date)
        const year = dateMoment.year()
        const month = dateMoment.month() // 0-based month
        const day = dateMoment.date()
        const dayOfWeek = dateMoment.day() // 0 = Sunday
        
        // New Year's Day
        if (month === 0 && day === 1) return true
        
        // Martin Luther King Jr. Day (3rd Monday in January)
        if (month === 0) {
            const firstMonday = moment([year, 0, 1]).startOf('month')
            while (firstMonday.day() !== 1) firstMonday.add(1, 'day')
            const thirdMonday = firstMonday.clone().add(14, 'days')
            if (day === thirdMonday.date()) return true
        }
        
        // Presidents' Day (3rd Monday in February)
        if (month === 1) {
            const firstMonday = moment([year, 1, 1])
            while (firstMonday.day() !== 1) firstMonday.add(1, 'day')
            const thirdMonday = firstMonday.clone().add(14, 'days')
            if (day === thirdMonday.date()) return true
        }
        
        // Memorial Day (last Monday in May)
        if (month === 4) {
            const lastMonday = moment([year, 4]).endOf('month') // Last day of May
            while (lastMonday.day() !== 1) lastMonday.subtract(1, 'day')
            if (day === lastMonday.date()) return true
        }
        
        // Independence Day
        if (month === 6 && day === 4) return true
        
        // Labor Day (1st Monday in September)
        if (month === 8) {
            const firstMonday = moment([year, 8, 1])
            while (firstMonday.day() !== 1) firstMonday.add(1, 'day')
            if (day === firstMonday.date()) return true
        }
        
        // Columbus Day (2nd Monday in October)
        if (month === 9) {
            const firstMonday = moment([year, 9, 1])
            while (firstMonday.day() !== 1) firstMonday.add(1, 'day')
            const secondMonday = firstMonday.clone().add(7, 'days')
            if (day === secondMonday.date()) return true
        }
        
        // Veterans Day
        if (month === 10 && day === 11) return true
        
        // Thanksgiving (4th Thursday in November)
        if (month === 10) {
            const firstThursday = moment([year, 10, 1])
            while (firstThursday.day() !== 4) firstThursday.add(1, 'day')
            const fourthThursday = firstThursday.clone().add(21, 'days')
            if (day === fourthThursday.date()) return true
        }
        
        // Christmas Day
        if (month === 11 && day === 25) return true
        
        return false
    }

    // Use API data or generate mock data as fallback
    const processedChartData = useMemo(() => {
        if (chartData && chartData.data && chartData.users) {
            // Use real API data with dynamic colors
            return {
                data: chartData.data.map(dayData => {
                    const processedDay = {
                        day: dayData.day,
                        date: dayData.date,
                        total: dayData.total,
                        isHoliday: isHoliday(moment(dayData.date).toDate()),
                        isWeekend: moment(dayData.date).day() === 0 || moment(dayData.date).day() === 6
                    }
                    
                    // Add user data
                    chartData.users.forEach(user => {
                        processedDay[user.key] = dayData[user.key] || 0
                    })
                    
                    return processedDay
                }),
                users: chartData.users.map((user, index) => ({
                    ...user,
                    color: generateColor(user, index, 'user')
                }))
            }
        }
        
        // Fallback to mock data
        const generateMockData = () => {
            const data = []
            const users = [
                { 
                    id: 'user_1', 
                    key: 'user_1', 
                    name: 'Loading...', 
                    color: generateColor({ name: 'Loading', key: 'user_1' }, 0, 'user')
                }
            ]

            // Generate simple loading data
            const startDate = dateRange.from ? moment(dateRange.from) : moment()
            const endDate = dateRange.to ? moment(dateRange.to) : moment()

            if (!dateRange.from) {
                startDate.subtract(6, 'days')
            }

            const currentDate = startDate.clone()
            
            while (currentDate.isSameOrBefore(endDate) && data.length < 14) {
                const dayName = currentDate.format('ddd')
                const monthDay = currentDate.format('MMM D')

                data.push({
                    day: `${dayName} ${monthDay}`,
                    date: currentDate.format('YYYY-MM-DD'),
                    total: 0,
                    isHoliday: false,
                    isWeekend: currentDate.day() === 0 || currentDate.day() === 6,
                    user_1: 0
                })

                currentDate.add(1, 'day')
            }

            return { data, users }
        }

        return generateMockData()
    }, [chartData, dateRange, currentTheme])

    // Calculate total hours for display
    const totalHours = useMemo(() => {
        if (!processedChartData.data || processedChartData.data.length === 0) {
            return "00:00:00"
        }
        
        const total = processedChartData.data.reduce((sum, day) => {
            return sum + day.total
        }, 0)
        
        const hours = Math.floor(total)
        const minutes = Math.round((total - hours) * 60)
        
        return `${hours.toLocaleString()}:${minutes.toString().padStart(2, '0')}:00`
    }, [processedChartData.data])

    const theme = {
        background: 'transparent',
        text: {
            fontSize: 11,
            fill: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-primary)'
        },
        axis: {
            domain: {
                line: {
                    stroke: 'var(--color-border-light)',
                    strokeWidth: 1
                }
            },
            legend: {
                text: {
                    fontSize: 12,
                    fill: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-family-primary)'
                }
            },
            ticks: {
                line: {
                    stroke: 'var(--color-border-light)',
                    strokeWidth: 1
                },
                text: {
                    fontSize: 10,
                    fill: 'var(--color-text-secondary)',
                    fontFamily: 'var(--font-family-primary)'
                }
            }
        },
        grid: {
            line: {
                stroke: 'var(--color-border-light)',
                strokeWidth: 0.5,
                strokeDasharray: '2 2'
            }
        },
        tooltip: {
            container: {
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
                fontSize: '12px',
                borderRadius: '6px',
                boxShadow: 'var(--shadow-lg)',
                border: '1px solid var(--color-border-light)'
            }
        }
    }

    // Custom tooltip
    const CustomTooltip = ({ id, value, color, data }) => {
        const user = processedChartData.users.find(u => u.key === id)
        const userName = user ? user.name : id
        
        return (
            <div className={styles.tooltip}>
                <div className={styles.tooltipHeader}>
                    <div 
                        className={styles.tooltipColor} 
                        style={{ backgroundColor: color }}
                    />
                    <span className={styles.tooltipUser}>
                        {userName}
                    </span>
                </div>
                <div className={styles.tooltipContent}>
                    <div className={styles.tooltipDate}>{data.day}</div>
                    <div className={styles.tooltipValue}>
                        {value}h logged
                    </div>
                </div>
            </div>
        )
    }

    // Custom layer to add holiday and weekend background
    const HolidayLayer = ({ bars, xScale }) => {
        return (
            <g>
                {processedChartData.data.map((data, index) => {
                    if (data.isHoliday || data.isWeekend) {
                        const barWidth = xScale.bandwidth()
                        const x = xScale(data.day)
                        return (
                            <rect
                                key={`holiday-weekend-${index}`}
                                x={x}
                                y={0}
                                width={barWidth}
                                height="100%"
                                fill={currentTheme === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.15)'}
                                pointerEvents="none"
                            />
                        )
                    }
                    return null
                })}
            </g>
        )
    }

    return (
        <div className={styles.chartContainer}>
            <div className={styles.chartHeader}>
                <div className={styles.chartTitle}>
                    <p>Time logged by team members over the selected period</p>
                </div>
                <div className={styles.chartTotal}>
                    <div className={styles.totalLabel}>Total:</div>
                    <div className={styles.totalValue}>{totalHours}</div>
                </div>
            </div>

            <div className={styles.chartWrapper}>
                <ResponsiveBar
                    data={processedChartData.data}
                    keys={processedChartData.users.map(u => u.key)}
                    indexBy="day"
                    margin={{ top: 20, right: 140, bottom: 80, left: 60 }}
                    padding={0.2}
                    innerPadding={1}
                    groupMode="stacked"
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    colors={({ id }) => {
                        const user = processedChartData.users.find(u => u.key === id)
                        return user ? user.color : '#8AC926'
                    }}
                    theme={theme}
                    borderRadius={2}
                    borderWidth={1}
                    borderColor="transparent"
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 8,
                        tickRotation: -45,
                        legendPosition: 'middle',
                        legendOffset: 60,
                        truncateTickAt: 0,
                        format: (value) => {
                            // Show only every 3rd tick to avoid overcrowding
                            const parts = value.split(' ')
                            if (parts.length > 1) {
                                const dayNum = parseInt(parts[2])
                                return dayNum % 3 === 1 ? value : ''
                            }
                            return value
                        }
                    }}
                    axisLeft={{
                        tickSize: 5,
                        tickPadding: 8,
                        tickRotation: 0,
                        legend: 'Hours',
                        legendPosition: 'middle',
                        legendOffset: -45,
                        format: (value) => `${value}h`
                    }}
                    enableGridY={true}
                    enableGridX={false}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    labelTextColor="white"
                    labelFormat={(value) => value > 1 ? `${value}h` : ''}
                    tooltip={CustomTooltip}
                    legends={[
                        {
                            dataFrom: 'keys',
                            anchor: 'bottom-right',
                            direction: 'column',
                            justify: false,
                            translateX: 120,
                            translateY: 0,
                            itemsSpacing: 2,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 12,
                            symbolShape: 'circle',
                            effects: [
                                {
                                    on: 'hover',
                                    style: {
                                        itemOpacity: 1
                                    }
                                }
                            ],
                            data: processedChartData.users.map(user => ({
                                id: user.key,
                                label: user.name,
                                color: user.color
                            }))
                        }
                    ]}
                    layers={['grid', 'axes', HolidayLayer, 'bars', 'markers', 'legends']}
                    animate={true}
                    motionConfig="gentle"
                />
            </div>
        </div>
    )
}